import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { businessesSearchInfiniteQueryOptions } from '@/api/queries/businesses';
import { usersSearchInfiniteQueryOptions } from '@/api/queries/users';
import { menuItemsSearchInfiniteQueryOptions } from '@/api/queries/menu-items';
import { mapBusinessWireToDomain } from '@/api/mappers/business';
import { mapUserWireToDomain } from '@/api/mappers/user';
import type { Business } from '@/types/business';
import type { User } from '@/types/user';
import type { MenuItem } from '@/types/menu-item';
import type { SearchTab } from './use-search-state';

interface UseSearchResultsParams {
  debouncedQuery: string;
  activeTab: SearchTab;
}

export function useSearchResults({ debouncedQuery, activeTab }: UseSearchResultsParams) {
  const trimmedQuery = debouncedQuery.trim();

  const businessQuery = useInfiniteQuery({
    ...businessesSearchInfiniteQueryOptions({
      query: trimmedQuery,
      per_page: 20,
    }),
    enabled: activeTab === 'places' && trimmedQuery.length >= 2,
  });

  const userQuery = useInfiniteQuery({
    ...usersSearchInfiniteQueryOptions({
      query: trimmedQuery,
      per_page: 20,
    }),
    enabled: activeTab === 'people' && trimmedQuery.length >= 2,
  });

  const menuItemQuery = useInfiniteQuery({
    ...menuItemsSearchInfiniteQueryOptions({
      query: trimmedQuery,
      per_page: 20,
    }),
    enabled: activeTab === 'menu-items' && trimmedQuery.length >= 1,
  });

  const businesses = useMemo<Business[]>(
    () => businessQuery.data?.pages.flatMap((p) => p.items.map(mapBusinessWireToDomain)) ?? [],
    [businessQuery.data],
  );

  const users = useMemo<User[]>(
    () => userQuery.data?.pages.flatMap((p) => p.items.map(mapUserWireToDomain)) ?? [],
    [userQuery.data],
  );

  const menuItems = useMemo<MenuItem[]>(
    () => menuItemQuery.data?.pages.flatMap((p) => p.items) ?? [],
    [menuItemQuery.data],
  );

  const activeQuery =
    activeTab === 'places' ? businessQuery
    : activeTab === 'people' ? userQuery
    : menuItemQuery;

  const totalResults = activeQuery.data?.pages[0]?.total ?? 0;

  return {
    businesses,
    users,
    menuItems,
    totalResults,
    isLoading: activeQuery.isLoading,
    error: activeQuery.error,
    refetch: activeQuery.refetch,
    fetchNextPage: activeQuery.fetchNextPage,
    hasNextPage: activeQuery.hasNextPage ?? false,
    isFetchingNextPage: activeQuery.isFetchingNextPage,
  };
}
